import { Search, Plus, X } from "lucide-react";
import type { MouseEvent, RefObject } from "react";
import { BrailleSpinnerCentered } from "./primitive/BrailleSpinner";
import { EmptyState, SessionGroup } from "./SessionsRailRows";
import type { ConversationSummary } from "./session-history-utils";

interface SessionsRailProps {
  search: string;
  onSearchChange: (value: string) => void;
  onClose: () => void;
  onNewSession: () => void;
  loading: boolean;
  grouped: Record<string, ConversationSummary[]>;
  flatSessions: ConversationSummary[];
  selectedIndex: number;
  setSelectedIndex: (index: number) => void;
  currentConversationId?: string;
  onSelectSession: (id: string) => void;
  onDeleteSession: (id: string, event: MouseEvent) => void;
  searchRef: RefObject<HTMLInputElement | null>;
  listRef: RefObject<HTMLDivElement | null>;
  variant?: "popup" | "shell";
}

export function SessionsRail({
  search,
  onSearchChange,
  onClose,
  onNewSession,
  loading,
  grouped,
  flatSessions,
  selectedIndex,
  setSelectedIndex,
  currentConversationId,
  onSelectSession,
  onDeleteSession,
  searchRef,
  listRef,
  variant = "popup",
}: SessionsRailProps) {
  const hasSessions = flatSessions.length > 0;
  const shell = variant === "shell";

  return (
    <div
      className={`relative overflow-hidden text-[var(--fintheon-text)] ${
        shell
          ? "h-full rounded-none bg-transparent"
          : "fintheon-dropdown-surface fintheon-session-history-popup max-h-[min(420px,calc(100vh-96px))] rounded-[18px]"
      }`}
      data-session-history-surface={variant}
    >
      <RailLine />
      <SearchRow
        search={search}
        onSearchChange={onSearchChange}
        onClose={onClose}
        onNewSession={onNewSession}
        searchRef={searchRef}
      />
      <div className="relative min-h-0">
        <div
          ref={listRef}
          className={`${shell ? "h-[calc(100%-42px)]" : "max-h-[360px]"} overflow-y-auto py-1`}
          style={{ scrollbarWidth: "thin" }}
        >
          {loading ? <LoadingState /> : null}
          {!loading && !hasSessions ? <EmptyState search={search} /> : null}
          {!loading && hasSessions
            ? Object.entries(grouped).map(([label, sessions]) => {
                const startIndex = flatSessions.indexOf(sessions[0]);
                return (
                  <SessionGroup
                    key={label}
                    label={label}
                    sessions={sessions}
                    startIndex={startIndex}
                    selectedIndex={selectedIndex}
                    currentConversationId={currentConversationId}
                    setSelectedIndex={setSelectedIndex}
                    onSelectSession={onSelectSession}
                    onDeleteSession={onDeleteSession}
                  />
                );
              })
            : null}
        </div>
        {hasSessions ? <BottomFade shell={shell} /> : null}
      </div>
    </div>
  );
}

function SearchRow({
  search,
  onSearchChange,
  onClose,
  onNewSession,
  searchRef,
}: {
  search: string;
  onSearchChange: (value: string) => void;
  onClose: () => void;
  onNewSession: () => void;
  searchRef: RefObject<HTMLInputElement | null>;
}) {
  return (
    <>
      <div className="flex items-center gap-2 px-3 py-2">
        <Search size={13} className="shrink-0 text-[var(--fintheon-muted)]" />
        <input
          ref={searchRef}
          type="text"
          value={search}
          onChange={(event) => onSearchChange(event.target.value)}
          placeholder="Search conversations..."
          className="flex-1 bg-transparent text-[12px] text-[var(--fintheon-text)] outline-none placeholder:text-[var(--fintheon-muted)]/45"
        />
        <button
          type="button"
          onClick={onNewSession}
          className="flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] text-[var(--fintheon-accent)] transition-colors hover:text-[var(--fintheon-text)]"
          title="New conversation"
        >
          <Plus size={11} />
          New
        </button>
        <button
          type="button"
          onClick={onClose}
          className="grid h-5 w-5 place-items-center rounded-full text-[var(--fintheon-muted)] transition-colors hover:text-[var(--fintheon-accent)]"
          title="Close sessions"
        >
          <X size={13} />
        </button>
      </div>
      <FadingRule />
    </>
  );
}

function LoadingState() {
  return (
    <div className="flex items-center justify-center py-8">
      <BrailleSpinnerCentered size={12} label="" />
    </div>
  );
}

function FadingRule({ active = false }: { active?: boolean }) {
  return (
    <div
      className={`h-px ${active ? "bg-[var(--fintheon-accent)]/24" : "bg-[var(--fintheon-accent)]/12"}`}
      style={{
        maskImage:
          "linear-gradient(to right, transparent, black 16%, black 84%, transparent)",
      }}
    />
  );
}

function RailLine() {
  return (
    <div
      className="pointer-events-none absolute bottom-2 left-0 top-2 w-px bg-[var(--fintheon-accent)]/24"
      style={{
        maskImage:
          "linear-gradient(to bottom, transparent, black 15%, black 85%, transparent)",
      }}
    />
  );
}

function BottomFade({ shell }: { shell: boolean }) {
  return (
    <div className="pointer-events-none absolute inset-x-0 bottom-0 h-16">
      {[15, 35, 65, 95].map((opacity) => (
        <div
          key={opacity}
          className={`h-4 ${
            shell
              ? "bg-[var(--fintheon-sidebar-expanded-surface)]"
              : "bg-[var(--fintheon-bg)]"
          }`}
          style={{ opacity: opacity / 100 }}
        />
      ))}
    </div>
  );
}
